import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { GenericTable } from '@shared/components/GenericTable';
import { TableColumn } from '@shared/types/table';
import { Badge } from '@shared/ui/badge';
import { Button } from '@shared/ui/button';
import { Plus } from 'lucide-react';
import { ServiceOrderDialogue } from '../ServiceOrderDialogue';
import { useRemoteUtilityId } from '@shared/selectors/globalSelectors';
import { useWorkOrderJobCards } from '@features/mx/operations/hooks';

interface ServiceOrdersProps {
  operation?: any;
}

export const ServiceOrders: React.FC<ServiceOrdersProps> = ({ operation }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const { operationId } = useParams();
  const remoteUtilityId = useRemoteUtilityId();

  const isCompleted =
    operation?.status === 2 ||
    operation?.status === 4;

  const limit = 10;
  const { data: jobCardsResponse, isLoading } = useWorkOrderJobCards(remoteUtilityId, operationId || '', page, limit);

  const data = (jobCardsResponse?.results || []).map((jobCard: any) => ({
    orderId: jobCard.jobCardNo || 'N/A',
    serviceType: jobCard.sopOperationTypeDisplay || 'N/A',
    status: jobCard.statusDisplay || 'N/A',
    assignedTo: jobCard.fieldForceName || 'N/A',
    createdDate: jobCard.createdDate || 'N/A',
    priority: jobCard.priorityDisplay || 'N/A',
  }));

  const columns: TableColumn<any>[] = [
    {
      key: 'orderId',
      header: 'Service Order ID',
      render: (val) => <span className="font-semibold text-blue-600">{val}</span>,
    },
    { key: 'serviceType', header: 'Service Type' },
    {
      key: 'status',
      header: 'Status',
      render: (val) => (
        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
          {val}
        </Badge>
      ),
    },
    { key: 'assignedTo', header: 'Assigned To' },
    { key: 'createdDate', header: 'Created Date' },
    {
      key: 'priority',
      header: 'Priority',
      render: (val) => (
        <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
          {val}
        </Badge>
      ),
    },
  ];

  const totalItems = jobCardsResponse?.count || 0;
  const pagination = {
    currentPage: page,
    totalPages: Math.ceil(totalItems / limit),
    totalItems: totalItems,
    itemsPerPage: limit,
    startItem: totalItems === 0 ? 0 : (page - 1) * limit + 1,
    endItem: Math.min(page * limit, totalItems),
  };

  return (
    <div className="space-y-4 pt-2">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Linked Service Orders</h3>
        <Button onClick={() => setIsDialogOpen(true)} disabled={isCompleted} className="">
          <Plus className="w-4 h-4" />
          Create Service Order
        </Button>
      </div>

      <GenericTable
        data={data}
        columns={columns}
        rowKey="orderId"
        emptyMessage="No linked service orders."
        loading={isLoading}
        pagination={pagination}
        onPageChange={setPage}
      />

      <ServiceOrderDialogue
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        consumerId="1"
        consumerName="N/A"
        consumerAccountNumber="N/A"
        requestId="1"
        remoteUtilityId={remoteUtilityId}
      />
    </div>
  );
};
